import React, { useEffect, useState } from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { API_DELETE, API_GET, DBRequest, useConfig } from '../../../api/api';
import useFetch from '../../../api/useFetch';
import DeleteIcon from '@mui/icons-material/Delete';
import MoneyLabel from '../../label/MoneyLabel';
import { tokens } from '../../../global/theme/tokens';
import UserStack from '../UserStack';
import NoDataAnimation from '../../animation/NoDataAnimation';

export const EmployeeClassSelector = ({ onSelect, existingEmpClasses }) => {
  const { t } = useTranslation();
  const config = useConfig();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  // Only fetch global wage presets for selection
  const { apiData, setApiData } = useFetch('wage-presets?isGlobal[eq]=true');
  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    if (!apiData) return;

    let newData = apiData.data;

    // Filter out global presets that are already assigned to the project
    // Project presets have parentId pointing to the global preset they were copied from
    newData = newData.filter((globalPreset) => {
      if (
        existingEmpClasses &&
        existingEmpClasses.find((existing) => existing.parentId == globalPreset.id)
      )
        return false;
      else return true;
    });

    setFilteredData(newData);
  }, [apiData, existingEmpClasses]);

  function handleDelete(e, preset) {
    e.stopPropagation();
    DBRequest({
      config,
      path: `wage-presets/${preset.id}`,
      method: API_DELETE,
      onResponse: () => handleDeleteResponse(preset),
    });
  }

  function handleDeleteResponse(preset) {
    const newData = filteredData.filter((el) => el.id != preset.id);
    setFilteredData(newData);
    // Also update the original apiData to keep in sync
    if (apiData) {
      setApiData({ ...apiData, data: apiData.data.filter((el) => el.id != preset.id) });
    }
  }

  function handleSelect(preset) {
    DBRequest({
      config,
      path: `wage-presets/${preset.id}`,
      method: API_GET,
      onResponse: onSelect,
    });
  }

  return (
    <Box className="px-5 pb-5">
      <Typography className="py-5" variant="h3">
        {t('Select Calculative Hourly Rate')}
      </Typography>
      {filteredData.length > 0 ? (
        <Box className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredData.map((preset) => (
            <Box
              key={preset.id}
              className="relative flex p-2 items-start gap-2 h-[94px] cursor-pointer transition-all"
              onClick={() => handleSelect(preset)}
              sx={{
                backgroundColor: preset.color + '30',
                borderRadius: 4,
                border: `1px solid ${preset.color}80`,
                '&:hover': {
                  backgroundColor: preset.color + '50',
                  transform: 'scale(1.02)',
                },
              }}
            >
              {preset.image && (
                <img
                  className="w-[32px] h-[32px] object-contain"
                  src={preset.image.temporaryUrl}
                  alt={preset.label}
                />
              )}
              <Box className="w-full">
                <Typography fontWeight={600}>{preset.label}</Typography>
                <Box className="flex justify-between gap-2">
                  <Typography>
                    <MoneyLabel money={preset.wage} /> {' / h'}
                  </Typography>
                </Box>
                <Box className="flex pt-1">
                  <UserStack users={preset.users || []} />
                </Box>
              </Box>
              <IconButton
                className="absolute right-1 top-1"
                size="small"
                onClick={(e) => handleDelete(e, preset)}
                sx={{
                  opacity: 0.6,
                  '&:hover': {
                    opacity: 1,
                    color: colors.redAccent?.[500] || '#f44336',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      ) : (
        <Box className="flex justify-center py-8">
          <NoDataAnimation />
        </Box>
      )}
    </Box>
  );
};
